import { useCallback, useEffect, useRef, useState } from 'react'
import {
  deck,
  deckCumulativeSecondsAt,
  deckRemainingFrom,
  deckTotalSeconds,
  formatDuration,
  TARGET_SECONDS,
} from '../content'
import { Visual } from '../components/visuals'
import { Callouts } from '../components/SceneParts'
import type { PresentationState } from './usePresentation'
import { PresenterWindow } from './presenterWindow'

/**
 * Presentation Mode.
 *
 * Built for live delivery in person or via Teams, which is what the RFP schedule
 * specifies. Sparse enough to narrate without reading the screen.
 */
export function Stage({ presentation }: { presentation: PresentationState }) {
  const { scene, section, index, next, prev, goTo, exit, notesVisible, toggleNotes } = presentation
  const stageRef = useRef<HTMLDivElement>(null)
  /* The transition line is spoken only on the first scene of a presentation section. */
  const transitionIn = deck[index - 1]?.section.id !== section.id ? section.transitionIn : undefined

  const elapsed = deckCumulativeSecondsAt[index] ?? 0
  const progress = ((index + 1) / deck.length) * 100

  /* Move focus into the stage so keyboard control works immediately. */
  useEffect(() => {
    stageRef.current?.focus()
  }, [])

  /* Preload the next scene's visual by rendering it hidden. Keeps advances instant. */
  const upcoming = deck[index + 1]?.scene

  /*
   * Recording support. The proposal is delivered as a recorded video, so the presenter view
   * lives in a second window and `clean` strips this window of everything but the scene.
   */
  const [clean, setClean] = useState(false)
  const [presenterOpen, setPresenterOpen] = useState(false)
  const presenterRef = useRef<PresenterWindow | null>(null)

  const payload = useCallback(
    () => ({
      scene,
      index,
      total: deck.length,
      chapterTitle: section.title,
      transitionIn,
      elapsedEstimate: elapsed,
      totalEstimate: deckTotalSeconds,
      scriptedRemaining: deckRemainingFrom(index),
      target: TARGET_SECONDS,
      next: upcoming ? { title: upcoming.title, headline: upcoming.headline } : undefined,
    }),
    [scene, index, section, transitionIn, elapsed, upcoming],
  )

  const togglePresenter = useCallback(() => {
    if (!presenterRef.current) {
      presenterRef.current = new PresenterWindow((dir) => (dir === 1 ? next() : prev()))
    }
    const pw = presenterRef.current
    if (pw.isOpen) {
      pw.close()
      setPresenterOpen(false)
      return
    }
    const opened = pw.open(payload())
    setPresenterOpen(opened)
    if (!opened) {
      window.alert(
        'The presenter window was blocked. Allow pop-ups for this site, then try again. ' +
          'Until then, press N for inline notes — but remember those appear in a screen recording.',
      )
    }
  }, [next, prev, payload])

  /* Keep the presenter window on the current scene. */
  useEffect(() => {
    const pw = presenterRef.current
    if (pw?.isOpen) pw.update(payload())
  }, [payload])

  /* Close the second window when the stage goes away. */
  useEffect(() => () => presenterRef.current?.close(), [])

  /* `C` toggles the clean recording view. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return
      if (e.key === 'c' || e.key === 'C') {
        e.preventDefault()
        setClean((v) => !v)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div
      className={`stage${clean ? ' is-clean' : ''}`}
      ref={stageRef}
      tabIndex={-1}
      role="region"
      aria-label="Presentation mode"
    >
      <div className="stage__bar" aria-hidden="true">
        <span className="stage__bar-fill" style={{ width: `${progress}%` }} />
      </div>

      <header className="stage__head">
        <span className="stage__chapter">{section.title}</span>
        <span className="stage__counter">
          <span className="mono">{String(index + 1).padStart(2, '0')}</span>
          <span className="stage__counter-sep">/</span>
          <span className="mono">{deck.length}</span>
          <span className="stage__scene-id mono">{scene.id}</span>
        </span>
        <div className="stage__actions">
          <button
            type="button"
            className="stage__btn"
            onClick={togglePresenter}
            aria-pressed={presenterOpen}
            title="Open presenter notes in a second window — keeps them out of a screen recording"
          >
            Presenter view
          </button>
          <button
            type="button"
            className="stage__btn"
            onClick={() => setClean((v) => !v)}
            aria-pressed={clean}
            title="Hide all controls for recording (C)"
          >
            Clean
          </button>
          <button
            type="button"
            className="stage__btn"
            onClick={toggleNotes}
            aria-pressed={notesVisible}
            title="Toggle inline presenter notes (N) — these DO appear in a screen recording"
          >
            Notes
          </button>
          <button type="button" className="stage__btn" onClick={exit} title="Leave presentation (Esc)">
            Exit
          </button>
        </div>
      </header>

      <main
        className={`stage__scene stage__scene--${scene.stageLayout ?? 'split'}${
          notesVisible && !presenterOpen ? ' has-notes' : ''
        }`}
        onClick={(e) => {
          /* Click to advance, but never swallow a click on a control. */
          if ((e.target as HTMLElement).closest('button, a, summary, details')) return
          next()
        }}
      >
        {scene.stageLayout === 'hero' ? (
          <>
            <div className="stage__visual">
              <Visual id={scene.visual} />
            </div>
            <div className="stage__copy">
              <h1 className="visually-hidden">{scene.headline}</h1>
              {scene.supporting ? <p className="stage__supporting">{scene.supporting}</p> : null}
            </div>
          </>
        ) : (
          <>
            <div className="stage__copy">
              <h1 className="stage__headline">{scene.headline}</h1>
              {scene.supporting ? <p className="stage__supporting">{scene.supporting}</p> : null}
              {scene.callouts?.length ? <Callouts items={scene.callouts} /> : null}
            </div>
            <div className="stage__visual">
              <Visual id={scene.visual} />
            </div>
          </>
        )}
      </main>

      {notesVisible && !presenterOpen ? (
        <aside className="notes" aria-label="Presenter notes">
          <div className="notes__head">
            <span className="notes__title">Presenter notes</span>
            <span className="notes__timing mono">
              {formatDuration(elapsed)} in · {scene.estimatedSeconds}s this scene ·{' '}
              {formatDuration(deckRemainingFrom(index))} left · {formatDuration(deckTotalSeconds)} of{' '}
              {formatDuration(TARGET_SECONDS)} target
            </span>
          </div>
          {transitionIn ? (
            <p className="notes__transition">
              <span className="notes__transition-label">Transition in</span>
              {transitionIn}
            </p>
          ) : null}
          <ul className="notes__list">
            {scene.presenterNotes.map((note, i) => (
              <li key={i}>{note}</li>
            ))}
          </ul>
          {upcoming ? (
            <p className="notes__next">
              <span className="notes__next-label">Next</span>
              {upcoming.title} — {upcoming.headline}
            </p>
          ) : (
            <p className="notes__next">
              <span className="notes__next-label">Next</span>Questions.
            </p>
          )}
        </aside>
      ) : null}

      <footer className="stage__foot">
        <button type="button" className="stage__nav" onClick={prev} disabled={index === 0} aria-label="Previous scene">
          <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
            <path d="M10 3 5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <ol className="stage__pips">
          {deck.map(({ scene: s }, i) => (
            <li key={s.id}>
              <button
                type="button"
                className={`stage__pip${i === index ? ' is-current' : ''}${i < index ? ' is-past' : ''}`}
                onClick={() => goTo(i)}
                aria-label={`Scene ${i + 1}: ${s.title}`}
                aria-current={i === index ? 'true' : undefined}
                title={`${s.id} — ${s.title}`}
              />
            </li>
          ))}
        </ol>
        <button
          type="button"
          className="stage__nav"
          onClick={next}
          disabled={index === deck.length - 1}
          aria-label="Next scene"
        >
          <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
            <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </footer>

      {/* Preloads the next visual so advancing never shows a loading frame. */}
      {upcoming ? (
        <div className="stage__preload" aria-hidden="true">
          <Visual id={upcoming.visual} />
        </div>
      ) : null}

      <p className="stage__hint">
        <span className="mono">←</span> <span className="mono">→</span> or <span className="mono">Space</span> to
        navigate · <span className="mono">N</span> for notes · <span className="mono">Esc</span> to exit
      </p>
    </div>
  )
}
